import React, { useMemo } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { MAINTENANCE } from "utils/token";
import { useRacehorse360Api } from "hooks/api";
import RadioButtonsFilter from "components/Filters/RadioButtonsFilter";
import CheckboxesFilter from "components/Filters/CheckboxesFilter";
import AutocompleteFilter from "components/Filters/AutocompleteFilter";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import Loader from "components/Loader";
import { IFilterDataItem } from "components/Filters/filtersOptions";
import { createAllValuesItem } from "components/Filters/helper";
import SliderFilter from "components/Filters/SliderFilter";
import {
  dates,
  MIN_DISTANCE,
  MAX_SLIDER_FILTER_VALUE,
  MIN_SLIDER_FILTER_VALUE,
  reportersTitles
} from "../filterOptions";
import {
  IAssignedFiltersState,
  IOpenFiltersState,
  ICompleteFiltersState,
  IClosedFiltersState,
  ICanceledFiltersState
} from "../../useLocalStateFilters";
import { getTabsStatuses } from "../../helper";

interface IProps {
  activeTab: racehorse360.WorkOrderStatus;
  openFiltersState: Partial<IOpenFiltersState>;
  assignedFiltersState: Partial<IAssignedFiltersState>;
  completeFiltersState: Partial<ICompleteFiltersState>;
  closedFiltersState: Partial<IClosedFiltersState>;
  canceledFiltersState: Partial<ICanceledFiltersState>;
}

const FiltersContent = React.memo((props: IProps) => {
  const {
    activeTab,
    openFiltersState: {
      openDate,
      setOpenDate,
      openOrderTypes,
      setOpenOrderTypes,
      openReporters,
      setOpenReporters,
      timeOpen,
      setTimeOpen
    },
    assignedFiltersState: {
      assignedDate,
      setAssignedDate,
      assignedTo,
      setAssignedTo,
      assignedOrderTypes,
      setAssignedOrderTypes,
      assignedReporters,
      setAssignedReporters,
      timeAssigned,
      setTimeAssigned
    },
    completeFiltersState: {
      setCompleteOrderTypes,
      completeOrderTypes,
      setCompleteAssignedTo,
      completeAssignedTo,
      setCompletedDate,
      setCompleteAssignedDate,
      completeAssignedDate,
      completedDate,
      completeTimeAssigned,
      setCompleteTimeAssigned
    },
    closedFiltersState: {
      setClosedReporters,
      setClosedOrderTypes,
      closedOrderTypes,
      setClosedAssignedTo,
      closedAssignedTo,
      setClosedDate,
      closedDate,
      closedReporters,
      closedTimeOpen,
      setClosedTimeOpen,
      closedTimeAssigned,
      setClosedTimeAssigned
    },
    canceledFiltersState: {
      setCanceledOrderTypes,
      canceledOrderTypes,
      canceledDate,
      setCanceledAssignedTo,
      canceledAssignedTo,
      setCanceledOpenDate,
      canceledOpenDate,
      setCanceledReporters,
      canceledReporters,
      setCanceledDate,
      canceledTimeAssigned,
      setCanceledTimeAssigned
    }
  } = props;
  const {
    isOpenTab,
    isAssignedTab,
    isCompleteTab,
    isClosedTab,
    isCanceledTab
  } = getTabsStatuses(activeTab);

  const { currentUser } = useLoggedInUser();
  const isSupervisor = currentUser.isMaintenanceSupervisor;
  const userRole = currentUser.roles.find(
    role => role.id === MAINTENANCE.SUPERVISOR || role.id === MAINTENANCE.STAFF
  );

  const {
    usePullWorkOrderReporters,
    usePullWorkOrderAssignedUsers,
    useListWorkOrderTypes
  } = useRacehorse360Api();

  const getPreparedData = (
    data: racehorse360.IUser[] = []
  ): IFilterDataItem[] => {
    return data.map((item: racehorse360.IUser): IFilterDataItem => {
      return {
        label: `${item.firstName} ${item.lastName}`,
        value: item.id
      };
    });
  };

  const { data: reportersData, isLoading: isLoadingReportersData } =
    usePullWorkOrderReporters(
      {
        facilityId: userRole?.facilityId,
        workOrderStatus: activeTab
      },
      {
        enabled: Boolean(userRole?.facilityId && !isCompleteTab)
      }
    );

  const { data: assignedUsersData, isLoading: isLoadingAssignedUsersData } =
    usePullWorkOrderAssignedUsers(
      {
        facilityId: userRole?.facilityId,
        workOrderStatus: activeTab
      },
      {
        enabled: Boolean(userRole?.facilityId && !isOpenTab)
      }
    );

  const { data: listWorkOrderTypes, isLoading: isWorkOrderTypesLoading } =
    useListWorkOrderTypes(
      {
        query: {},
        getOptions: {
          select: ["id", "name"],
          orderBy: ["name"]
        },
        pagingOptions: {
          maxResults: 999
        }
      },
      {
        onError: error => {
          console.error(
            error?.message ||
              "There was an error attempting to request list of workorder types."
          );
        }
      }
    );

  const workOrderTypes = listWorkOrderTypes?.workOrderTypes;

  const preparedReporters: IFilterDataItem[] = useMemo(() => {
    return getPreparedData(reportersData?.users);
  }, [reportersData]);

  const preparedAssignedUsers: IFilterDataItem[] = useMemo(() => {
    return getPreparedData(assignedUsersData?.users);
  }, [assignedUsersData]);

  const preparedWorkOrderTypes: IFilterDataItem[] = useMemo(
    () =>
      workOrderTypes && [
        ...workOrderTypes.map(
          (item: racehorse360.IWorkOrderType): IFilterDataItem => {
            return {
              label: item.name,
              value: item.id
            };
          }
        )
      ],
    [workOrderTypes]
  );

  const isLoading =
    isLoadingReportersData ||
    isLoadingAssignedUsersData ||
    isWorkOrderTypesLoading;

  return isLoading ? (
    <Loader />
  ) : (
    <>
      {isOpenTab && (
        <>
          <RadioButtonsFilter
            title="Open Dates"
            data={dates}
            selectedValue={openDate}
            onChangeSelectedValue={setOpenDate}
          />
          <SliderFilter
            maxValue={MAX_SLIDER_FILTER_VALUE}
            minValue={MIN_SLIDER_FILTER_VALUE}
            minDistance={MIN_DISTANCE}
            selectedValues={timeOpen}
            onChangeSelectedValues={setTimeOpen}
            filterTitle="Time Open"
          />
          <CheckboxesFilter
            title="Order Type"
            data={[
              createAllValuesItem("Order Types"),
              ...preparedWorkOrderTypes
            ]}
            selectedValues={openOrderTypes}
            onChangeSelectedValues={setOpenOrderTypes}
            withLimit={true}
          />
          <AutocompleteFilter
            selectedValues={openReporters}
            onChangeSelectedValues={setOpenReporters}
            titles={reportersTitles}
            data={preparedReporters}
          />
        </>
      )}

      {isAssignedTab && (
        <>
          <RadioButtonsFilter
            title="Assigned Dates"
            data={dates}
            selectedValue={assignedDate}
            onChangeSelectedValue={setAssignedDate}
          />
          <SliderFilter
            maxValue={MAX_SLIDER_FILTER_VALUE}
            minValue={MIN_SLIDER_FILTER_VALUE}
            minDistance={MIN_DISTANCE}
            selectedValues={timeAssigned}
            onChangeSelectedValues={setTimeAssigned}
            filterTitle="Time Assigned"
          />
          {isSupervisor && (
            <CheckboxesFilter
              title="Assigned To"
              data={[
                createAllValuesItem("Assignees"),
                ...preparedAssignedUsers
              ]}
              selectedValues={assignedTo}
              onChangeSelectedValues={setAssignedTo}
              maxWidthPreview={350}
              withLimit={true}
            />
          )}
          <CheckboxesFilter
            title="Order Type"
            data={[
              createAllValuesItem("Order Types"),
              ...preparedWorkOrderTypes
            ]}
            selectedValues={assignedOrderTypes}
            onChangeSelectedValues={setAssignedOrderTypes}
            withLimit={true}
          />
          <AutocompleteFilter
            selectedValues={assignedReporters}
            onChangeSelectedValues={setAssignedReporters}
            titles={reportersTitles}
            data={preparedReporters}
          />
        </>
      )}

      {isCompleteTab && (
        <>
          <RadioButtonsFilter
            title="Assigned Dates"
            data={dates}
            selectedValue={completeAssignedDate}
            onChangeSelectedValue={setCompleteAssignedDate}
          />
          <SliderFilter
            maxValue={MAX_SLIDER_FILTER_VALUE}
            minValue={MIN_SLIDER_FILTER_VALUE}
            minDistance={MIN_DISTANCE}
            selectedValues={completeTimeAssigned}
            onChangeSelectedValues={setCompleteTimeAssigned}
            filterTitle="Time Assigned"
          />
          <RadioButtonsFilter
            title="Completed Dates"
            data={dates}
            selectedValue={completedDate}
            onChangeSelectedValue={setCompletedDate}
          />
          {isSupervisor && (
            <CheckboxesFilter
              title="Assigned To"
              data={[
                createAllValuesItem("Assignees"),
                ...preparedAssignedUsers
              ]}
              selectedValues={completeAssignedTo}
              onChangeSelectedValues={setCompleteAssignedTo}
              maxWidthPreview={350}
              withLimit={true}
            />
          )}
          <CheckboxesFilter
            title="Order Type"
            data={[
              createAllValuesItem("Order Types"),
              ...preparedWorkOrderTypes
            ]}
            selectedValues={completeOrderTypes}
            onChangeSelectedValues={setCompleteOrderTypes}
            withLimit={true}
          />
        </>
      )}

      {isClosedTab && (
        <>
          <AutocompleteFilter
            selectedValues={closedReporters}
            onChangeSelectedValues={setClosedReporters}
            titles={reportersTitles}
            data={preparedReporters}
          />
          <SliderFilter
            maxValue={MAX_SLIDER_FILTER_VALUE}
            minValue={MIN_SLIDER_FILTER_VALUE}
            minDistance={MIN_DISTANCE}
            selectedValues={closedTimeOpen}
            onChangeSelectedValues={setClosedTimeOpen}
            filterTitle="Time Open"
          />
          {isSupervisor && (
            <CheckboxesFilter
              title="Assigned To"
              data={[
                createAllValuesItem("Assignees"),
                ...preparedAssignedUsers
              ]}
              selectedValues={closedAssignedTo}
              onChangeSelectedValues={setClosedAssignedTo}
              maxWidthPreview={350}
              withLimit={true}
            />
          )}
          <SliderFilter
            maxValue={MAX_SLIDER_FILTER_VALUE}
            minValue={MIN_SLIDER_FILTER_VALUE}
            minDistance={MIN_DISTANCE}
            selectedValues={closedTimeAssigned}
            onChangeSelectedValues={setClosedTimeAssigned}
            filterTitle="Time Assigned"
          />
          <RadioButtonsFilter
            title="Closed Dates"
            data={dates}
            selectedValue={closedDate}
            onChangeSelectedValue={setClosedDate}
          />
          <CheckboxesFilter
            title="Order Type"
            data={[
              createAllValuesItem("Order Types"),
              ...preparedWorkOrderTypes
            ]}
            selectedValues={closedOrderTypes}
            onChangeSelectedValues={setClosedOrderTypes}
            withLimit={true}
          />
        </>
      )}

      {isCanceledTab && (
        <>
          <AutocompleteFilter
            selectedValues={canceledReporters}
            onChangeSelectedValues={setCanceledReporters}
            titles={reportersTitles}
            data={preparedReporters}
          />
          <RadioButtonsFilter
            title="Opened Dates"
            data={dates}
            selectedValue={canceledOpenDate}
            onChangeSelectedValue={setCanceledOpenDate}
          />
          {isSupervisor && (
            <CheckboxesFilter
              title="Assigned To"
              data={[
                createAllValuesItem("Assignees"),
                ...preparedAssignedUsers
              ]}
              selectedValues={canceledAssignedTo}
              onChangeSelectedValues={setCanceledAssignedTo}
              maxWidthPreview={350}
              withLimit={true}
            />
          )}
          <SliderFilter
            maxValue={MAX_SLIDER_FILTER_VALUE}
            minValue={MIN_SLIDER_FILTER_VALUE}
            minDistance={MIN_DISTANCE}
            selectedValues={canceledTimeAssigned}
            onChangeSelectedValues={setCanceledTimeAssigned}
            filterTitle="Time Assigned"
          />
          <RadioButtonsFilter
            title="Canceled Dates"
            data={dates}
            selectedValue={canceledDate}
            onChangeSelectedValue={setCanceledDate}
          />
          <CheckboxesFilter
            title="Order Type"
            data={[
              createAllValuesItem("Order Types"),
              ...preparedWorkOrderTypes
            ]}
            selectedValues={canceledOrderTypes}
            onChangeSelectedValues={setCanceledOrderTypes}
            withLimit={true}
          />
        </>
      )}
    </>
  );
});

export default FiltersContent;
